import { useState, useCallback } from "react";
import type { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";

/**
 * Options for useDataTableState hook
 */
export interface UseDataTableStateOptions {
  /** Initial page number (0-indexed). Default: 0 */
  initialPage?: number;
  /** Initial page size. Default: 50 */
  initialPageSize?: number;
  /** Initial sort configuration */
  initialSort?: GridSortModel;
  /** Reset page to 0 when sort changes (typical for server-side). Default: true */
  resetPageOnSort?: boolean;
}

/**
 * Return type for useDataTableState hook
 */
export interface UseDataTableStateReturn {
  /** Current pagination state */
  paginationModel: GridPaginationModel;
  /** Current sort state */
  sortModel: GridSortModel;
  /** Handler for pagination changes */
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /** Handler for sort changes */
  onSortModelChange: (model: GridSortModel) => void;
  /** Reset pagination to initial values */
  resetPagination: () => void;
  /** Set page directly */
  setPage: (page: number) => void;
  /** Set page size directly */
  setPageSize: (size: number) => void;
}

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 50;

/**
 * Hook for managing DataTable pagination and sorting state.
 *
 * @example
 * // Server-side pagination (resets page on sort)
 * const tableState = useDataTableState({
 *   initialPageSize: 50,
 *   initialSort: [{ field: "name", sort: "asc" }],
 * });
 *
 * // Use with DataTable
 * <DataTable {...tableState} />
 *
 * // Access values for API calls
 * const { page, pageSize } = tableState.paginationModel;
 * const sortField = tableState.sortModel[0]?.field;
 */
export function useDataTableState(
  options?: UseDataTableStateOptions
): UseDataTableStateReturn {
  const {
    initialPage = DEFAULT_PAGE,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialSort = [],
    resetPageOnSort = true,
  } = options ?? {};

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: initialPage,
    pageSize: initialPageSize,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>(initialSort);

  const onPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      // Server-side behavior: reset to first page when sorting changes
      if (resetPageOnSort) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    },
    [resetPageOnSort]
  );

  const resetPagination = useCallback(() => {
    setPaginationModel({ page: initialPage, pageSize: initialPageSize });
  }, [initialPage, initialPageSize]);

  const setPage = useCallback((page: number) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setPaginationModel((prev) => ({ ...prev, pageSize, page: 0 }));
  }, []);

  return {
    paginationModel,
    sortModel,
    onPaginationModelChange,
    onSortModelChange,
    resetPagination,
    setPage,
    setPageSize,
  };
}
